import { createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { checkSession, SESSION_EXPIRED_REJECT } from "../../utils/sessionCookie"
import { read as readAttribution } from "../../utils/attribution"
import { buildTempCabs } from "../../utils/tempRates"
import { isComingSoon } from "../../config/launch"

const RATE_LIMIT_MESSAGE =
  "Rate limit exceeded. Maximum 10 requests per 3 hours allowed for this IP address."

const GET_CABS_CACHE_TTL = 30000 // 30 seconds: prevents duplicate DB visitor entries on redirect/re-render

const normalizeGetCabsPayload = (data = {}) => {
  const { signal, ...payload } = data || {}

  return Object.keys(payload)
    .sort()
    .reduce((acc, key) => {
      const value = payload[key]
      if (value === undefined || typeof value === "function") return acc

      if (value && typeof value === "object") {
        acc[key] = value.label || value.value || JSON.stringify(value)
      } else {
        acc[key] = value === null ? "" : String(value).trim()
      }

      return acc
    }, {})
}

const buildGetCabsRequestKey = (data = {}) =>
  JSON.stringify(normalizeGetCabsPayload(data))

const pendingGetCabsRequests = new Map()
const recentGetCabsResponses = new Map()

const getRecentGetCabsResponse = (requestKey) => {
  const cached = recentGetCabsResponses.get(requestKey)
  if (!cached) return null

  if (Date.now() - cached.time > GET_CABS_CACHE_TTL) {
    recentGetCabsResponses.delete(requestKey)
    return null
  }

  return cached.response
}

const setRecentGetCabsResponse = (requestKey, response) => {
  recentGetCabsResponses.set(requestKey, {
    time: Date.now(),
    response,
  })
}

const getCabs = createAsyncThunk(
  "cabCabs",
  async (data, { rejectWithValue }) => {
    if (typeof window !== "undefined" && !checkSession()) {
      return rejectWithValue(SESSION_EXPIRED_REJECT)
    }

    const { signal, ...payload } = data || {}
    const requestKey = buildGetCabsRequestKey(payload)

    try {
      const recentResponse = getRecentGetCabsResponse(requestKey)
      if (recentResponse) {
        return recentResponse
      }

      if (pendingGetCabsRequests.has(requestKey)) {
        return await pendingGetCabsRequests.get(requestKey)
      }

      console.log("req get cab payload>>", payload)

      const config = { headers: { "Content-Type": "application/json" } }

      // Do not attach AbortController signal here.
      // React StrictMode / route re-render can abort after the backend already inserted visitor entry.
      // Keeping one shared promise + short cache ensures only one backend hit for the same search.
      // Additive: a backend that doesn't read `attribution` ignores it. This is
      // the only moment the ad click can be tied to an enquiry, because the
      // enquiry row is created by this very call.
      const requestPromise = axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/website/get_trip_fare`,
        { ...payload, attribution: readAttribution() },
        config
      )

      pendingGetCabsRequests.set(requestKey, requestPromise)
      const response = await requestPromise

      const serverMessage = response?.data?.message || ""
      const isRateLimit =
        serverMessage.includes("Rate limit") ||
        serverMessage.includes("Maximum") ||
        serverMessage === RATE_LIMIT_MESSAGE
      const hasFareData = response?.data?.data != null

      if (isRateLimit && !hasFareData) {
        return rejectWithValue(serverMessage || RATE_LIMIT_MESSAGE)
      }

      // TEMP: the fleets backend has only partial rate config for local/rental
      // (one cab type each, no Gujarat rental), so fill all three cab types
      // in-site for trip_type 1 (local) and 2 (rental). Outstation (3) keeps its
      // real backend cabs. Uses the real visitorEntryId/web_trip_unique_id from
      // this response so booking still works. Remove once the backend returns
      // all cab types for these products. See src/utils/tempRates.js.
      //
      // While a product is in "coming soon" mode (config/launch.js) we do NOT
      // inject placeholder fares: the booking widget never enters the funnel for
      // it, and if a stray request slips through it must not carry a placeholder
      // price to the live payment page.
      const tempCabs = isComingSoon(payload?.trip_type)
        ? null
        : buildTempCabs(payload?.trip_type, payload)
      if (tempCabs && response?.data) {
        response.data.data = tempCabs
      }

      setRecentGetCabsResponse(requestKey, response)
      return response
    } catch (err) {
      if (axios.isCancel(err)) {
        return rejectWithValue(null)
      }
      return rejectWithValue(err.response?.data?.message || err.message)
    } finally {
      pendingGetCabsRequests.delete(requestKey)
    }
  }
)

const getCustomerFAQ = createAsyncThunk(
  "customerFAQ",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_CMS_URL || "https://customerapi.pulpitcabs.com"}/website/get_customer_faqs`
      )
      return response
    } catch (error) {
      return rejectWithValue(error.response.data.message)
    }
  }
)

const bookNow = createAsyncThunk(
  "bookNow",
  async (data, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "application/json" } }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/website/book_now`,
        data,
        config
      )
      return response
    } catch (error) {
      return rejectWithValue(error.response.data.message)
    }
  }
)

const createOrder = createAsyncThunk(
  "createOrder",
  async (data, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "application/json" } }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/website/createOrder`,
        data,
        config
      )
      return response
    } catch (error) {
      return rejectWithValue(error.response.data.message)
    }
  }
)

const applyCoupon = createAsyncThunk(
  "applyCoupon",
  async (data, { rejectWithValue }) => {
    try {
      const config = { headers: { "Content-Type": "application/json" } }
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/website/apply_coupon`,
        data,
        config
      )
      return response
    } catch (error) {
      return rejectWithValue(error.response.data.message)
    }
  }
)

export { getCabs, bookNow, createOrder, applyCoupon, getCustomerFAQ }
